'use client';

import { ReactNode, useCallback } from 'react';
import clsx from 'clsx';
import { PRODUCT_INFORMATION_MENU_ID } from '@headless-workspace/config';
import { useUIContext } from '@headless-workspace/core-ui';
import { GlowButton, GlowModal, PropsWithFontFamily } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';

export enum ProductDetailInfoModalContext {
    storeSelection = 'store-selection',
}

type ProductDetailInfoModalProps = Partial<PropsWithFontFamily> & {
    renderInformation: ReactNode;
    title: string;
    closeButtonLabel?: string;
    actionButtonLabel?: string;
    actionButtonHref?: string;
    productModalId?: string;
    hasBackCta?: boolean;
    context?: ProductDetailInfoModalContext;
    isSidePanelDeliveryZone?: boolean;
    removeModalOnClose?: boolean;
};

export const ProductDetailInfoModal: Component<ProductDetailInfoModalProps> = ({
    renderInformation,
    title,
    closeButtonLabel,
    actionButtonLabel,
    actionButtonHref,
    productModalId,
    fontFamily = 'brand',
    hasBackCta = false,
    isSidePanelDeliveryZone = false,
    removeModalOnClose = false,
    context,
}) => {
    const {
        menuModal: { close: closeMenu },
    } = useUIContext();

    const hasTwoButtons = actionButtonLabel && actionButtonHref;

    const closeProductInformationModal = useCallback(
        (shouldClose = false) =>
            () => {
                if (shouldClose || context !== ProductDetailInfoModalContext.storeSelection) {
                    closeMenu(productModalId ?? PRODUCT_INFORMATION_MENU_ID, removeModalOnClose);
                }
            },
        [closeMenu, context, productModalId, removeModalOnClose],
    );

    return (
        <GlowModal.Container
            title={title}
            onClickBackCta={hasBackCta ? closeProductInformationModal(true) : undefined}
            onClick={closeProductInformationModal(true)}
            id={'product-detail-info-modal'}
            className={clsx(isSidePanelDeliveryZone && 'desktopS:w-[44.25rem]')}
            fontFamily={fontFamily}
        >
            {renderInformation}
            {closeButtonLabel && (
                <GlowModal.Footer>
                    {hasTwoButtons ? (
                        <>
                            <GlowButton
                                href={actionButtonHref}
                                label={actionButtonLabel}
                                fontFamily={fontFamily}
                                width={'full'}
                            />
                            <GlowButton
                                variant={'secondary'}
                                onClick={closeProductInformationModal(false)}
                                width={'full'}
                                label={closeButtonLabel}
                                fontFamily={fontFamily}
                            />
                        </>
                    ) : (
                        <GlowButton
                            onClick={closeProductInformationModal(false)}
                            label={closeButtonLabel}
                            fontFamily={fontFamily}
                            width={'full'}
                        />
                    )}
                </GlowModal.Footer>
            )}
        </GlowModal.Container>
    );
};
